import { html, css, LitElement } from 'lit';
import videoThumbnail from '../../../assets/images/videoPlaceholder.svg';
import AudioPlaceholder from '../../../assets/images/equalizer.svg'
import '../../../stories/Components/Loader/Spinner/Spinner';
import'../../../components/ChatUIComponent/AudioBubble';
import '../../../stories/Components/SharePopup/SharePopup';
import { WEB_CONTENT } from '../../../constants/content';
import { drawerDialogService } from '../Dialog-Drawer/dialog-drawer';

class MediaGallery extends LitElement {
  static properties = {
    allAttachments: {
      type: Array,
      hasChanged(newVal, oldVal) {
        return JSON.stringify(newVal) !== JSON.stringify(oldVal);
      },
    },
    mainImageIndex: { type: Number },
    topbarBgColor: { type: String },
    iconColor: { type: String },
    galleryImageSize: { type: String },
    isZoomed: { type: Boolean },
    loadingMediaGallery: { type: Boolean },
    zoomLevel: { type: Number },
    isSharePopupOpen: { type: Boolean },
    shareLink: { type: String },
  };

  static styles = css`
    :host {
      display: block;
      font-family: Arial, sans-serif;
    }

    .media-gallery-container {
      width: 100%;
      height: 100%;
      z-index: 1000;
      display: flex;
      flex-direction: column;
    }

    .topbar {
      display: flex;
      justify-content: flex-end;
      background-color: var(--bg-secondary-color);
      padding: 10px;
    }

    .topbar-icon-container {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .icon {
      cursor: pointer;
      color: var(--text-primary-color);
    }

    .popup-overlay {
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-color: var(--overlay-color);
      z-index: 999;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .main-image-container {
      text-align: center;
      padding: 10px;
      background-color: var(--bg-primary-color);
      height: 50%;
      position: relative;
      touch-action: pan-y pinch-zoom;
      flex: 1;
      cursor: zoom-in;
      overflow: hidden;
    }

    .main-image-container.zoomed {
      cursor: move;
    }

    .main-image {
      max-width: 100%;
      height: 100%;
      border-radius: 5px;
      transition: transform 0.2s ease;
      object-fit: contain;
    }

    .zoom-controls {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.6);
      border-radius: 20px;
      padding: 5px 10px;
      display: flex;
      align-items: center;
      gap: 10px;
      z-index: 10;
    }

    .zoom-btn {
      background: none;
      border: none;
      color: white;
      font-size: 20px;
      cursor: pointer;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .zoom-level {
      color: white;
      font-size: 14px;
      min-width: 50px;
      text-align: center;
    }

    .nav-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0, 0, 0, 0.5);
      color: white;
      padding: 15px 10px;
      cursor: pointer;
      border: none;
      border-radius: 4px;
      font-size: 20px;
      z-index: 1;
    }

    .nav-arrow:hover {
      background: rgba(0, 0, 0, 0.7);
    }

    .nav-arrow.left {
      left: 10px;
    }
    .nav-arrow.right {
      right: 10px;
    }

    .gallery {
      display: flex;
      gap: 10px;
      background-color: var(--bg-secondary-color);
      padding: 10px;
      overflow-x: auto;
      scrollbar-width: thin;
    }

    .gallery::-webkit-scrollbar {
      height: 6px;
    }

    .gallery::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.3);
      border-radius: 3px;
    }

    .gallery-image,
    .gallery-video-container {
      width: var(--gallery-image-size, 70px);
      height: var(--gallery-image-size, 70px);
      flex-shrink: 0;
      border-radius: 5px;
    }

    .gallery-image {
      object-fit: cover;
      cursor: pointer;
      transition: transform 0.2s;
      background-color: var(--base-color);
    }

    .gallery-image:hover,
    .gallery-video-container:hover {
      transform: scale(1.05);
    }

    .gallery-image.selected,
    .gallery-video-container.selected {
      transform: scale(1.1);
      outline: 3px solid var(--base-color);
      outline-offset: 2px;
    }

    .unsupported-media {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      font-size: 20px;
      color: #808080;
    }

    .video-thumbnail {
      width: 70px;
      height: 70px;
    }

    .spinner-wrapper {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      justify-content: center;
      align-items: center;
    }
    
    .audio-wrapper{
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    @media (max-width: 768px) {
      .main-image { max-height: 100%; }
      .gallery-image, .gallery-video-container {
        width: calc(var(--gallery-image-size, 70px) - 10px);
        height: calc(var(--gallery-image-size, 70px) - 10px);
      }
    }
  `;

  constructor() {
    super();
    this.mainImageIndex = 0;
    this.topbarBgColor = '#f1f1f1';
    this.iconColor = 'var(--text-primary-color)';
    this.galleryImageSize = '70px';
    this.isZoomed = false;
    this.loadingMediaGallery = false;
    this._mediaItems = null;
    this._boundKeyHandler = this.handleKeyPress.bind(this);
    this._attachmentsHash = '';
    
    // Zoom related properties
    this.zoomLevel = 1;
    this.minZoom = 1;
    this.maxZoom = 5;
    this.zoomStep = 0.5;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.initialPinchDistance = 0;
    
    // Image boundaries for constrained panning
    this.imageBounds = {
      width: 0,
      height: 0
    };
    this.containerBounds = {
      width: 0,
      height: 0
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._boundKeyHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', this._boundKeyHandler);
    this._removeZoomListeners();
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('allAttachments')) {
      this.resetMediaItemsCache();
    }

    if (changedProperties.has('mainImageIndex')) {
      // Reset zoom when changing images
      this.resetZoom();
    }

    if (changedProperties.has('isZoomed')) {
      const mainContainer = this.shadowRoot.querySelector('.main-image-container');
      if (this.isZoomed) {
        mainContainer.classList.add('zoomed');
        this._setupZoomListeners();
        this._updateBoundingBoxes();
      } else {
        mainContainer.classList.remove('zoomed');
        this._removeZoomListeners();
        this.resetZoom();
      }
    }
    
    if (changedProperties.has('zoomLevel')) {
      this._updateBoundingBoxes();
    }
  }
  
  _updateBoundingBoxes() {
    requestAnimationFrame(() => {
      const container = this.shadowRoot.querySelector('.main-image-container');
      const image = this.shadowRoot.querySelector('.main-image');
      
      if (!container || !image) return;
      
      // Get the displayed dimensions of the image (not zoomed)
      const displayedWidth = image.offsetWidth / this.zoomLevel;
      const displayedHeight = image.offsetHeight / this.zoomLevel;
      
      // Calculate the zoomed dimensions
      this.imageBounds = {
        width: displayedWidth * this.zoomLevel,
        height: displayedHeight * this.zoomLevel
      };
      
      this.containerBounds = {
        width: container.clientWidth,
        height: container.clientHeight
      };
    });
  }

  _calculateAttachmentsHash(attachments) {
    return JSON.stringify(attachments);
  }

  _processAttachments() {
    if (!this.allAttachments) return [];

    return this.allAttachments.reduce((acc, message) => {
      if (message.attachments?.length) {
        const validAttachments = message.attachments.filter(
          attachment => attachment.mimeType?.match(/^(image|video|audio)\//)
        );
        return [...acc, ...validAttachments];
      }
      return acc;
    }, []);
  }

  get mediaItems() {
    const currentHash = this._calculateAttachmentsHash(this.allAttachments);

    if (this._mediaItems === null || currentHash !== this._attachmentsHash) {
      this._attachmentsHash = currentHash;
      this._mediaItems = this._processAttachments();
    }

    return this._mediaItems;
  }

  resetMediaItemsCache() {
    this._mediaItems = null;
    this._attachmentsHash = '';
  }

  handleKeyPress(event) {
    const currentMedia = this.mediaItems[this.mainImageIndex];
    const isImage = currentMedia?.mimeType?.startsWith('image/');

    switch (event.key) {
      case 'ArrowLeft':
        this.navigateImage('prev');
        break;
      case 'ArrowRight':
        this.navigateImage('next');
        break;
      case 'Escape':
        if (this.isZoomed) {
          this.resetZoom();
        } else {
          this.closeGallery();
        }
        break;
      case '+':
        if (isImage) this.zoomIn();
        break;
      case '-':
        if (isImage) this.zoomOut();
        break;
      case ' ':
      case 'Enter':
        if (isImage) this.toggleZoom();
        break;
    }
  }

  navigateImage(direction) {
    const newIndex = direction === 'next' ? Math.min(this.mainImageIndex + 1, this.mediaItems.length - 1) : Math.max(this.mainImageIndex - 1, 0);

    if (newIndex !== this.mainImageIndex) {
      this.isZoomed = false;
      this.mainImageIndex = newIndex;
    }
  }

  toggleZoom() {
    this.isZoomed = !this.isZoomed;
    
    if (this.isZoomed) {
      this.zoomLevel = 1.5; // Start with slight zoom
      this._updateBoundingBoxes();
      this.updateZoomStyle();
    } else {
      this.resetZoom();
    }
  }

  resetZoom() {
    this.zoomLevel = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.isZoomed = false;
    this.updateZoomStyle();
  }

  zoomIn(e) {
    if (e) e.stopPropagation();
    
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
      this._updateBoundingBoxes();
      this.constrainTranslation();
      this.updateZoomStyle();
    }
  }

  zoomOut(e) {
    if (e) e.stopPropagation();
    
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
      
      // If we're back at min zoom, reset position
      if (this.zoomLevel === this.minZoom) {
        this.translateX = 0;
        this.translateY = 0;
      } else {
        this._updateBoundingBoxes();
        this.constrainTranslation();
      }
      
      this.updateZoomStyle();
    }
  }
  
  // New method to constrain the translation within visible bounds
  constrainTranslation() {
    // Calculate how much the image extends beyond the container on each side when zoomed
    const overflowX = Math.max(0, this.imageBounds.width - this.containerBounds.width);
    const overflowY = Math.max(0, this.imageBounds.height - this.containerBounds.height);
    
    // Ensure a minimum percentage of the image remains visible (e.g., 20%)
    const minVisibleWidth = this.containerBounds.width * 0.2;
    const minVisibleHeight = this.containerBounds.height * 0.2;
    
    // Calculate maximum allowed translation
    // This allows the image to move until only the minimum visible portion remains
    const maxTranslateX = overflowX / 2 + (this.containerBounds.width - minVisibleWidth) / 2;
    const maxTranslateY = overflowY / 2 + (this.containerBounds.height - minVisibleHeight) / 2;
    
    // Constrain the translation values
    this.translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.translateX));
    this.translateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.translateY));
  }

  updateZoomStyle() {
    const mainImage = this.shadowRoot.querySelector('.main-image');
    if (!mainImage) return;

    mainImage.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoomLevel})`;
    
    // Only show zoomed cursor when zoomed in
    const container = this.shadowRoot.querySelector('.main-image-container');
    if (container) {
      if (this.zoomLevel > 1) {
        container.classList.add('zoomed');
      } else {
        container.classList.remove('zoomed');
      }
    }
    
    this.requestUpdate();
  }

  _setupZoomListeners() {
    const container = this.shadowRoot.querySelector('.main-image-container');
    const mainImage = this.shadowRoot.querySelector('.main-image');
    
    if (!container || !mainImage) return;
    
    // Mouse events for desktop
    this._boundMouseDown = this.handleMouseDown.bind(this);
    this._boundMouseMove = this.handleMouseMove.bind(this);
    this._boundMouseUp = this.handleMouseUp.bind(this);
    this._boundWheel = this.handleWheel.bind(this);
    
    // Touch events for mobile
    this._boundTouchStart = this.handleTouchStart.bind(this);
    this._boundTouchMove = this.handleTouchMove.bind(this);
    this._boundTouchEnd = this.handleTouchEnd.bind(this);
    
    container.addEventListener('mousedown', this._boundMouseDown);
    window.addEventListener('mousemove', this._boundMouseMove);
    window.addEventListener('mouseup', this._boundMouseUp);
    container.addEventListener('wheel', this._boundWheel);
    
    container.addEventListener('touchstart', this._boundTouchStart, { passive: false });
    container.addEventListener('touchmove', this._boundTouchMove, { passive: false });
    container.addEventListener('touchend', this._boundTouchEnd);
    
    // Update bounding boxes on window resize
    this._boundResizeHandler = this._updateBoundingBoxes.bind(this);
    window.addEventListener('resize', this._boundResizeHandler);
    
    // Initial update of bounding boxes
    this._updateBoundingBoxes();
  }

  _removeZoomListeners() {
    const container = this.shadowRoot.querySelector('.main-image-container');
    
    if (!container) return;
    
    if (this._boundMouseDown) {
      container.removeEventListener('mousedown', this._boundMouseDown);
      window.removeEventListener('mousemove', this._boundMouseMove);
      window.removeEventListener('mouseup', this._boundMouseUp);
      container.removeEventListener('wheel', this._boundWheel);
      
      container.removeEventListener('touchstart', this._boundTouchStart);
      container.removeEventListener('touchmove', this._boundTouchMove);
      container.removeEventListener('touchend', this._boundTouchEnd);
    }
    
    if (this._boundResizeHandler) {
      window.removeEventListener('resize', this._boundResizeHandler);
    }
  }

  handleMouseDown(e) {
    const currentMedia = this.mediaItems[this.mainImageIndex];
    const isImage = currentMedia?.mimeType?.startsWith('image/');
    
    if (!isImage || this.zoomLevel <= 1) return;
    
    this.isDragging = true;
    this.startX = e.clientX - this.translateX;
    this.startY = e.clientY - this.translateY;
    e.preventDefault();
  }

  handleMouseMove(e) {
    if (!this.isDragging) return;
    
    const newTranslateX = e.clientX - this.startX;
    const newTranslateY = e.clientY - this.startY;
    
    this.translateX = newTranslateX;
    this.translateY = newTranslateY;
    
    // Apply constraints to keep the image in view
    this.constrainTranslation();
    this.updateZoomStyle();
    e.preventDefault();
  }

  handleMouseUp() {
    this.isDragging = false;
  }

  handleWheel(e) {
    e.preventDefault();
    
    const currentMedia = this.mediaItems[this.mainImageIndex];
    const isImage = currentMedia?.mimeType?.startsWith('image/');
    
    if (!isImage) return;
    
    // Get cursor position relative to container for zoom targeting
    const container = this.shadowRoot.querySelector('.main-image-container');
    const rect = container.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    
    // Store old zoom level
    const oldZoom = this.zoomLevel;
    
    if (e.deltaY < 0) {
      // Zoom in
      this.zoomIn();
    } else {
      // Zoom out
      this.zoomOut();
    }
    
    // If zoom level actually changed and we're zoomed in
    if (oldZoom !== this.zoomLevel && this.zoomLevel > 1) {
      // Adjust translateX and translateY to zoom in/out around cursor position
      // This is a simplified approach - for more precise targeting, you'd need more complex math
      this.constrainTranslation();
    }
  }

  handleTouchStart(e) {
    const currentMedia = this.mediaItems[this.mainImageIndex];
    const isImage = currentMedia?.mimeType?.startsWith('image/');
    
    if (!isImage) return;
    
    e.preventDefault();
    
    if (e.touches.length === 1) {
      // Single touch - prepare for panning
      this.isDragging = true;
      this.startX = e.touches[0].clientX - this.translateX;
      this.startY = e.touches[0].clientY - this.translateY;
    } 
    else if (e.touches.length === 2) {
      // Pinch to zoom
      this.isDragging = false;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      this.initialPinchDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
    }
  }

  handleTouchMove(e) {
    const currentMedia = this.mediaItems[this.mainImageIndex];
    const isImage = currentMedia?.mimeType?.startsWith('image/');
    
    if (!isImage || this.zoomLevel <= 1 && e.touches.length === 1) return;
    
    e.preventDefault();
    
    if (e.touches.length === 1 && this.isDragging) {
      // Pan the image
      this.translateX = e.touches[0].clientX - this.startX;
      this.translateY = e.touches[0].clientY - this.startY;
      
      // Apply constraints to keep the image in view
      this.constrainTranslation();
      this.updateZoomStyle();
    } 
    else if (e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      if (this.initialPinchDistance > 0) {
        const pinchRatio = currentDistance / this.initialPinchDistance;
        const newZoomLevel = Math.max(
          this.minZoom,
          Math.min(this.maxZoom, this.zoomLevel * pinchRatio)
        );
        
        // Calculate center point between the two touches
        const centerX = (touch1.clientX + touch2.clientX) / 2;
        const centerY = (touch1.clientY + touch2.clientY) / 2;
        
        // Apply zoom centered on pinch point
        this.zoomLevel = newZoomLevel;
        this.initialPinchDistance = currentDistance;
        
        this._updateBoundingBoxes();
        this.constrainTranslation();
        this.updateZoomStyle();
      }
    }
  }

  handleTouchEnd(e) {
    this.isDragging = false;
    this.initialPinchDistance = 0;
    
    // If we're at minimum zoom, reset position
    if (this.zoomLevel <= this.minZoom) {
      this.resetZoom();
    }
  }

  handleShare() {
    const currentMedia = this.mediaItems[this.mainImageIndex];
    if (!currentMedia) return;
    drawerDialogService.openDialog('share-popup', {
      titleColor:"var(--text-primary-color)",
            ctaColor:"var(--bg-primary-color)", 
              title:WEB_CONTENT.POPUP_MESSAGES.SHARE_MEDIA_LABEL,
              shareLink:currentMedia.mediaUrl
    });

  }


  async handleDownload() {
    const currentMedia = this.mediaItems[this.mainImageIndex];
    if (!currentMedia) return;

    const extension = currentMedia.mimeType?.includes('/') ? '.' + currentMedia.mimeType.split('/')[1] : '.jpg';

    try {
      const response = await fetch(currentMedia.mediaUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-media${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  }

  openShareWindow(url) {
    window.open(`mailto:?body=${encodeURIComponent(url)}`, '_blank');
  }

  closeGallery() {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }


  render() {
    const items = this.mediaItems;

    if (this.loadingMediaGallery && !items.length) {
      return html`<div class="spinner-wrapper">
        <spinner-loader
          height="15px"
          width="15px"
          spinTime="1s"
          spinnerPrimaryColor="var(--bg-primary-color)"
          spinnerSecondaryColor="var(--text-primary-color)"
          spinnerBorderWidth="2px"
        ></spinner-loader>
      </div>`;
    }

    if (!this.loadingMediaGallery && !items.length) {
      return html`<div class="unsupported-media">${WEB_CONTENT.PLACEHOLDER_CONTENT.UNSUPPORTED_MEDIA}</div>`;
    }

    const currentMedia = items[this.mainImageIndex];
    const isImage = currentMedia?.mimeType?.startsWith('image/');
    const isVideo = currentMedia?.mimeType?.startsWith('video/');
    const isAudio = currentMedia?.mimeType?.startsWith('audio/');

    return html`
      <div class="media-gallery-container">
        <div class="topbar">
          <div class="topbar-icon-container">
            ${!isAudio ? html` <span class="icon" @click=${this.handleShare}>
              <svg width="20" height="20" fill="var(--text-primary-color)" viewBox="0 0 1920 1920">
                <path
                  d="M1513.827 1278.28c-96.772 0-182.634 43.765-241.415 111.531l-564.2-325.737c11.807-33.498 19.508-69.049 19.508-106.654 0-34.91-7.06-68.022-17.327-99.595l563.815-325.48c58.782 66.482 143.746 109.35 239.619 109.35 177.243 0 320.86-143.618 320.86-320.86 0-177.244-143.617-320.86-320.86-320.86-177.243 0-320.86 143.616-320.86 320.86 0 35.165 7.059 68.407 17.454 99.98l-563.686 325.48C587.953 679.554 502.86 636.56 406.86 636.56 229.617 636.56 86 780.177 86 957.42c0 177.243 143.617 320.86 320.86 320.86 93.434 0 176.601-40.428 235.254-104.215l567.537 327.662c-9.882 30.803-16.684 63.145-16.684 97.413 0 177.243 143.617 320.86 320.86 320.86 177.243 0 320.86-143.617 320.86-320.86 0-177.243-143.617-320.86-320.86-320.86"
                />
              </svg>
            </span>` : null}
            <span class="icon" @click=${this.handleDownload}>
              <svg width="24" fill="var(--text-primary-color)" height="24" viewBox="0 0 24 24">
                <path
                  d="M9.163 2.819C9 3.139 9 3.559 9 4.4V11H7.803c-.883 0-1.325 0-1.534.176a.75.75 0 0 0-.266.62c.017.274.322.593.931 1.232l4.198 4.401c.302.318.453.476.63.535a.749.749 0 0 0 .476 0c.177-.059.328-.217.63-.535l4.198-4.4c.61-.64.914-.96.93-1.233a.75.75 0 0 0-.265-.62C17.522 11 17.081 11 16.197 11H15V4.4c0-.84 0-1.26-.164-1.581a1.5 1.5 0 0 0-.655-.656C13.861 2 13.441 2 12.6 2h-1.2c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656zM5 21a1 1 0 0 0 1 1h12a1 1 0 1 0 0-2H6a1 1 0 0 0-1 1z"
                />
              </svg>
            </span>
            <span class="icon"  @click=${this.closeGallery}>
              <svg width="24" height="24" fill="var(--text-primary-color)" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </span>
          </div>
        </div>

        <div class="main-image-container">
          ${this.mainImageIndex > 0
        ? html`<button class="nav-arrow left" @click=${e => (e.stopPropagation(), this.navigateImage('prev'))}>←</button>`
        : ''}
          
         ${
      isImage
        ? html`
          <img src="${currentMedia.mediaUrl}" alt="Main Image" class="main-image" @dblclick=${this.toggleZoom} />
          ${this.isZoomed && isImage ? html`
            <div class="zoom-controls">
              <button class="zoom-btn" @click=${this.zoomOut}>−</button>
              <span class="zoom-level">${Math.round(this.zoomLevel * 100)}%</span>
              <button class="zoom-btn" @click=${this.zoomIn}>+</button>
            </div>
          ` : ''}
          `
        : isVideo
          ? html`<video src="${currentMedia.mediaUrl}" class="main-image" controls autoplay></video>` 
          : isAudio 
            ? html`
              <div class='audio-wrapper' key=${currentMedia?.mediaUrl}>
                <audio-bubble
                  .audioUrl=${currentMedia?.mediaUrl}
                  .showFooter=${false}
                ></audio-bubble>
              </div>
              `
            : html`<div class="unsupported-media">${WEB_CONTENT.UNSUPPORTED_MEDIA_TYPES}</div>`}
          
         ${this.mainImageIndex < items.length - 1
        ? html`<button class="nav-arrow right" @click=${e => (e.stopPropagation(), this.navigateImage('next'))}>→</button>`
        : ''}
        </div>

        <div class="gallery">
          ${items.map((media, index) => {
            const isVideo = media.mimeType?.startsWith('video/');
            const isAudio = media.mimeType?.startsWith('audio/');
            return isVideo 
              ? html`
                <div class="gallery-video-container ${index === this.mainImageIndex ? 'selected' : ''}"
                     @click=${() => (this.mainImageIndex = index)}>
                  <img class='video-thumbnail' src="${videoThumbnail}"></img>
                </div>`
              : isAudio 
                ? html`
                  <img src="${AudioPlaceholder}" 
                       alt="Gallery Image ${index + 1}"
                       class="gallery-image ${index === this.mainImageIndex ? 'selected' : ''}"
                       @click=${() => (this.mainImageIndex = index)} />`
                : html`
                  <img src="${media.mediaUrl}" 
                       alt="Gallery Image ${index + 1}"
                       class="gallery-image ${index === this.mainImageIndex ? 'selected' : ''}"
                       @click=${() => (this.mainImageIndex = index)} />`;
          })}
        </div>
      </div>
    `;
  }
}

customElements.define('media-gallery', MediaGallery);

export { MediaGallery };
